// Home portal slots: banners between Home's rows that open a zone, driven by each spec's `homePortal` and its dates.
// One portal per slot (the highest `priority`, then the earliest `from`). A portal with its own `from` shows from then
// (it can announce a zone before its season: Open Haunt's banner goes up on the 20th, the zone opens on the 23rd);
// without one it follows the zone's season, gate included (accounts' clock decides). Either way it ends at its `to`,
// else the season's end.
//
// pages/home.ts calls `portalAfter("hero", cat)` after the carousel and `portalAfter(`row:${n}`, cat)` after its
// n-th row (1-based).

import type { Catalogue } from "../catalogue.ts";
import { promoBlock } from "./promo.ts";
import { allZones, portalRenderer } from "./registry.ts";
import { dateMs, promoOpen, type HomePortal, type Zone } from "./spec.ts";

export interface PlacedPortal {
  zone: Zone;
  portal: HomePortal;
}

/** The portal for each slot that has one now, as a map slot → portal. Pure: `zones` and `now` are injectable. */
export function homePortals(cat: Pick<Catalogue, "rows"> | null, now = Date.now(), zones: readonly Zone[] = allZones()): Map<string, PlacedPortal> {
  const rowIds = cat?.rows ? new Set(cat.rows.map((r) => r.id)) : undefined;
  const live = zones
    .filter((z): z is Zone & { homePortal: HomePortal } => !!z.homePortal)
    .filter((z) => portalOpen(z, now, rowIds))
    .sort((a, b) => b.homePortal.priority - a.homePortal.priority || (dateMs(a.homePortal.from) || 0) - (dateMs(b.homePortal.from) || 0) || a.id.localeCompare(b.id));
  const out = new Map<string, PlacedPortal>();
  for (const z of live) if (!out.has(z.homePortal.after)) out.set(z.homePortal.after, { zone: z, portal: z.homePortal });
  return out;
}

/** Is a zone's portal up now? */
export function portalOpen(z: Zone & { homePortal: HomePortal }, now: number, rowIds?: ReadonlySet<string>): boolean {
  return promoOpen(z.homePortal, z, now, rowIds);
}

/** The banner for one Home slot ("hero" or "row:<n>"), or null. */
export function portalAfter(slot: string, cat: Pick<Catalogue, "rows"> | null, now = Date.now()): HTMLElement | null {
  const p = homePortals(cat, now).get(slot);
  return p ? portalElement(p, now) : null;
}

/**
 * Home's blocks with the portals placed: `[hero, portal(hero), row 1, portal(row:1), row 2, …]`. Rows are counted as
 * drawn (null blocks don't count), so "row:2" is always after the second visible row, whatever Home's layout.
 */
export function withPortals(hero: HTMLElement | null, rows: (HTMLElement | null)[], cat: Pick<Catalogue, "rows"> | null, now = Date.now()): HTMLElement[] {
  const slots = homePortals(cat, now);
  const banner = (slot: string) => {
    const p = slots.get(slot);
    return p ? portalElement(p, now) : null;
  };
  const out: (HTMLElement | null)[] = [hero, hero ? banner("hero") : null];
  let n = 0;
  for (const r of rows) if (r) out.push(r, banner(`row:${++n}`));
  return out.filter((x): x is HTMLElement => !!x);
}

/** The zone's registered banner (registerPortal), else the default promo block in its colours. */
function portalElement(p: PlacedPortal, now: number): HTMLElement {
  let el: HTMLElement | null = null;
  try {
    el = portalRenderer(p.zone.id)?.(p.zone, now) ?? null;
  } catch (e) {
    console.warn(`zone ${p.zone.id}: portal failed, using the default`, e);
  }
  el ??= promoBlock(p.zone, "banner", p.portal.copy, p.portal.art, p.portal.cta, undefined, p.portal.title, p.portal.eyebrow);
  el.classList.add("home-portal");
  return el;
}
